import { ref, watch } from 'vue'
import axiosInstance from '@/axios'

/**
 * useArtifiProductViews -- fetch the CURRENT SKU's Artifi-configured
 * views so the editor drives the preview view_code from the product's
 * real views rather than the hardcoded SKU-18000 static map (bug #1884).
 *
 * Backed by GET /api/artifi/product_views. The backend normalizes
 * Artifi's ProductViews[] groups into a flat list; this composable also
 * carries the group name (Front / Back / ...) so callers can pick a
 * front-group default view.
 *
 * Response shape (from artifi_product_views.py):
 *
 *   {
 *     "sku": "K540",
 *     "views": [
 *       {"code": "Left Chest", "display_name": "Left Chest",
 *        "group": "Front", "thumbnail_url": null},
 *       ...
 *     ],
 *     "fetched_at": "..."
 *   }
 *
 * Public surface:
 *
 *   const { views, loading, error } = useArtifiProductViews(skuRef)
 *
 *   skuRef:   Ref<string|null>|() => string|null    // null -> no fetch
 *   views:    Ref<Array<{code, display_name, group, thumbnail_url}>>
 *   loading:  Ref<boolean>
 *   error:    Ref<{code,message}|null>
 *   notFound: Ref<boolean>                          // true iff the backend
 *                                                   // returned 404
 *                                                   // sku_not_found
 *
 * ``notFound`` (issue #2202) distinguishes the EXPECTED "Artifi does not
 * recognize this SKU" state (a 404 ``sku_not_found``) from a genuine
 * failure. Callers can surface a friendly "This SKU isn't recognized"
 * message instead of a red error banner, and MUST NOT treat it as a
 * retryable fault. A 404 is not console.error'd or retried by this
 * composable (the watcher only fetches on SKU change, never on error).
 *
 * Module-level cache keyed by SKU (matches useArtifiCoverage): multiple
 * components watching the same SKU share one axios request. Errors are
 * NOT cached (transient failure retries on next mount, and an empty
 * views list fails open to the legacy static map in the resolver).
 */
const ENDPOINT = '/api/artifi/product_views'

const _cache = new Map()
const _inflight = new Map()

function _fetch(sku) {
    if (_cache.has(sku)) {
        return Promise.resolve(_cache.get(sku))
    }
    if (_inflight.has(sku)) {
        return _inflight.get(sku)
    }
    const promise = axiosInstance
        .get(ENDPOINT, { params: { sku } })
        .then((resp) => {
            const data = resp.data || {}
            const views = Array.isArray(data.views) ? data.views : []
            const result = { views, error: null }
            _cache.set(sku, result)
            return result
        })
        .catch((err) => {
            const errPayload =
                err?.response?.data?.error || {
                    code: 'network_error',
                    message: err?.message || 'Network error',
                }
            // Do NOT cache errors -- fail open to the legacy static map.
            return { views: [], error: errPayload }
        })
        .finally(() => {
            _inflight.delete(sku)
        })
    _inflight.set(sku, promise)
    return promise
}

/**
 * One-shot read of a SKU's views (shared cache + in-flight dedup), for a
 * caller that needs ANOTHER style's views than the editor's current one --
 * the Change SKU dialog judging the NEW style (card #545). Resolves
 * ``{views, error}``; never rejects (an error fails open to ``views: []``).
 */
export function fetchArtifiProductViews(sku) {
    if (!sku || typeof sku !== 'string') {
        return Promise.resolve({ views: [], error: null })
    }
    return _fetch(sku)
}

/**
 * Test-only: clear the module-level cache so specs re-isolate per-test.
 */
export function _resetArtifiProductViewsCache() {
    _cache.clear()
    _inflight.clear()
}

function _readRef(skuRef) {
    if (typeof skuRef === 'function') return skuRef()
    return skuRef?.value ?? skuRef
}

export function useArtifiProductViews(skuRef) {
    const views = ref([])
    const loading = ref(false)
    const error = ref(null)
    const notFound = ref(false)

    watch(
        () => _readRef(skuRef),
        async (sku) => {
            if (!sku || typeof sku !== 'string') {
                views.value = []
                error.value = null
                notFound.value = false
                loading.value = false
                return
            }
            loading.value = true
            error.value = null
            notFound.value = false
            const result = await _fetch(sku)
            // Guard against a fast SKU switch clobbering with a stale result.
            if (_readRef(skuRef) !== sku) return
            views.value = result.views
            error.value = result.error
            // Issue #2202: a 404 sku_not_found is the expected "Artifi does
            // not recognize this SKU" state, not a fault. Flag it so the UI
            // can show a friendly message rather than an error banner.
            notFound.value = result.error?.code === 'sku_not_found'
            loading.value = false
        },
        { immediate: true },
    )

    return { views, loading, error, notFound }
}

export default useArtifiProductViews
